import React, { useState } from 'react';
import { Layout } from '../components/Layout/Layout';
import { HandshakeIcon, MapPinIcon, DollarSignIcon, CalendarIcon, UserIcon, MessageCircleIcon, StarIcon, PhoneIcon, MailIcon } from 'lucide-react';
export const DealMatchPage = () => {
  const [selectedDeal, setSelectedDeal] = useState<number | null>(1); // Default to first deal
  // Sample deals data - Updated for Malaysia
  const deals = [{
    id: 1,
    title: 'Hydroponic Vegetable Farm Investment',
    partner: {
      name: 'Green Future Malaysia',
      image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80',
      location: 'Cameron Highlands, Pahang',
      rating: 4.8,
      reviews: 24,
      about: 'Green Future Malaysia specializes in highland hydroponic farming with 5 years of successful operations. We focus on sustainable, pesticide-free produce for local markets and export to Singapore.'
    },
    status: 'pending',
    type: 'Investment',
    amount: 'RM 750,000',
    return: '15-18% ROI',
    timeline: '24 months',
    description: 'Opportunity to invest in our expansion of hydroponic facilities producing leafy greens and vegetables for local markets and export to Singapore. The investment will fund new growing systems and cold storage facilities.',
    matchScore: 92,
    messages: 3,
    lastActivity: '2 days ago'
  }, {
    id: 2,
    title: 'Organic Durian Orchard Partnership',
    partner: {
      name: 'Pahang Durian Collective',
      image: 'https://images.unsplash.com/photo-1560365163-3e8d64e762ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1964&q=80',
      location: 'Raub, Pahang',
      rating: 4.6,
      reviews: 18,
      about: 'A cooperative of 15 small-scale organic durian farmers working together since 2018. We grow Musang King, D24, and Black Thorn varieties using organic practices for local consumption and export to China.'
    },
    status: 'negotiating',
    type: 'Partnership',
    amount: 'RM 420,000',
    return: '12-14% ROI',
    timeline: '36 months',
    description: 'Seeking investment partner for our processing facility to produce value-added durian products including frozen durian, durian paste, and durian-based confectioneries. The facility will serve all cooperative members and increase profit margins.',
    matchScore: 87,
    messages: 8,
    lastActivity: '5 hours ago'
  }, {
    id: 3,
    title: 'Smart Irrigation Technology Implementation',
    partner: {
      name: 'AquaIntel Malaysia',
      image: 'https://images.unsplash.com/photo-1573497491765-dccce02b29df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80',
      location: 'MADA Region, Kedah',
      rating: 4.9,
      reviews: 31,
      about: 'AgTech company specializing in AI-driven irrigation solutions. Our systems have been implemented in over 30 farms across Malaysia, reducing water usage by an average of 35%.'
    },
    status: 'accepted',
    type: 'Technology Partnership',
    amount: 'RM 600,000',
    return: '16-20% ROI',
    timeline: '18 months',
    description: 'Partnership opportunity to implement our AI-driven irrigation technology in paddy fields within the MADA irrigation scheme. The system uses soil sensors, weather data, and machine learning to optimize irrigation scheduling and reduce water usage by up to 40%.',
    matchScore: 95,
    messages: 12,
    lastActivity: 'Yesterday'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'negotiating':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'negotiating':
        return 'In Negotiation';
      case 'accepted':
        return 'Deal Accepted';
      case 'rejected':
        return 'Deal Declined';
      default:
        return status;
    }
  };
  const selectedDealData = deals.find(deal => deal.id === selectedDeal);
  return <Layout data-id="element-625">
      <div className="container mx-auto px-4 py-8" data-id="element-626">
        <div className="mb-8" data-id="element-627">
          <h1 className="text-3xl font-bold text-soil-brown" data-id="element-628">Deal Matches</h1>
          <p className="text-gray-600 mt-1" data-id="element-629">
            Review and manage your potential agricultural partnerships
          </p>
        </div>
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-630">
          {/* Deals List */}
          <div className="lg:w-2/5 xl:w-1/3" data-id="element-631">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-632">
              <div className="p-4 border-b" data-id="element-633">
                <h2 className="font-semibold text-lg" data-id="element-634">Your Matches</h2>
              </div>
              <div className="divide-y" data-id="element-635">
                {deals.map(deal => <div key={deal.id} className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${selectedDeal === deal.id ? 'bg-leaf-green/5 border-l-4 border-leaf-green' : ''}`} onClick={() => setSelectedDeal(deal.id)} data-id="element-636">
                    <div className="flex justify-between items-start mb-2" data-id="element-637">
                      <h3 className="font-medium text-soil-brown" data-id="element-638">
                        {deal.title}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(deal.status)}`} data-id="element-639">
                        {getStatusLabel(deal.status)}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2" data-id="element-640">
                      <HandshakeIcon size={14} className="mr-1" data-id="element-641" />
                      <span data-id="element-642">{deal.type}</span>
                      <span className="mx-2" data-id="element-643">•</span>
                      <MapPinIcon size={14} className="mr-1" data-id="element-644" />
                      <span data-id="element-645">{deal.partner.location}</span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-646">
                      <div className="flex items-center" data-id="element-647">
                        <img src={deal.partner.image} alt={deal.partner.name} className="w-8 h-8 rounded-full object-cover mr-2" data-id="element-648" />
                        <span className="text-sm font-medium" data-id="element-649">
                          {deal.partner.name}
                        </span>
                      </div>
                      <div className="bg-leaf-green text-white text-xs font-semibold rounded-full px-2 py-1" data-id="element-650">
                        {deal.matchScore}% Match
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-3" data-id="element-651">
                      <div className="flex items-center" data-id="element-652">
                        <MessageCircleIcon size={12} className="mr-1" data-id="element-653" />
                        <span data-id="element-654">{deal.messages} messages</span>
                      </div>
                      <span data-id="element-655">Last activity: {deal.lastActivity}</span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Deal Details */}
          <div className="lg:w-3/5 xl:w-2/3" data-id="element-656">
            {selectedDealData && <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-657">
                <div className="p-4 border-b flex justify-between items-center" data-id="element-658">
                  <h2 className="font-semibold text-lg" data-id="element-659">Deal Details</h2>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(selectedDealData.status)}`} data-id="element-660">
                    {getStatusLabel(selectedDealData.status)}
                  </span>
                </div>
                <div className="p-6" data-id="element-661">
                  <h2 className="text-2xl font-semibold text-soil-brown mb-4" data-id="element-662">
                    {selectedDealData.title}
                  </h2>
                  {/* Partner Info */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-663">
                    <div className="flex items-start" data-id="element-664">
                      <img src={selectedDealData.partner.image} alt={selectedDealData.partner.name} className="w-16 h-16 rounded-lg object-cover mr-4" data-id="element-665" />
                      <div data-id="element-666">
                        <h3 className="font-semibold text-lg" data-id="element-667">
                          {selectedDealData.partner.name}
                        </h3>
                        <div className="flex items-center text-sm text-gray-600 mb-1" data-id="element-668">
                          <MapPinIcon size={14} className="mr-1" data-id="element-669" />
                          <span data-id="element-670">{selectedDealData.partner.location}</span>
                        </div>
                        <div className="flex items-center" data-id="element-671">
                          <div className="flex items-center mr-2" data-id="element-672">
                            <StarIcon size={14} className="text-yellow-500 mr-1" data-id="element-673" />
                            <span className="text-sm font-medium" data-id="element-674">
                              {selectedDealData.partner.rating}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500" data-id="element-675">
                            ({selectedDealData.partner.reviews} reviews)
                          </span>
                        </div>
                      </div>
                      <div className="ml-auto flex space-x-2" data-id="element-676">
                        <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200" aria-label="Message" data-id="element-677">
                          <MessageCircleIcon size={18} className="text-gray-600" data-id="element-678" />
                        </button>
                        <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200" aria-label="Call" data-id="element-679">
                          <PhoneIcon size={18} className="text-gray-600" data-id="element-680" />
                        </button>
                        <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200" aria-label="Email" data-id="element-681">
                          <MailIcon size={18} className="text-gray-600" data-id="element-682" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3" data-id="element-683">
                      <h4 className="font-medium mb-1" data-id="element-684">About</h4>
                      <p className="text-sm text-gray-600" data-id="element-685">
                        {selectedDealData.partner.about}
                      </p>
                    </div>
                  </div>
                  {/* Deal Info */}
                  <div className="mb-6" data-id="element-686">
                    <h3 className="font-semibold text-lg mb-3" data-id="element-687">
                      Deal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-688">
                      <div className="bg-leaf-green/10 rounded-md p-3" data-id="element-689">
                        <p className="text-xs text-gray-600" data-id="element-690">Deal Type</p>
                        <p className="font-semibold text-soil-brown" data-id="element-691">
                          {selectedDealData.type}
                        </p>
                      </div>
                      <div className="bg-leaf-green/10 rounded-md p-3" data-id="element-692">
                        <p className="text-xs text-gray-600" data-id="element-693">
                          Investment Amount
                        </p>
                        <div className="flex items-center" data-id="element-694">
                          <DollarSignIcon size={14} className="mr-1 text-soil-brown" data-id="element-695" />
                          <p className="font-semibold text-soil-brown" data-id="element-696">
                            {selectedDealData.amount}
                          </p>
                        </div>
                      </div>
                      <div className="bg-leaf-green/10 rounded-md p-3" data-id="element-697">
                        <p className="text-xs text-gray-600" data-id="element-698">Expected Return</p>
                        <p className="font-semibold text-soil-brown" data-id="element-699">
                          {selectedDealData.return}
                        </p>
                      </div>
                      <div className="bg-leaf-green/10 rounded-md p-3 col-span-1 md:col-span-2" data-id="element-700">
                        <p className="text-xs text-gray-600" data-id="element-701">Timeline</p>
                        <div className="flex items-center" data-id="element-702">
                          <CalendarIcon size={14} className="mr-1 text-soil-brown" data-id="element-703" />
                          <p className="font-semibold text-soil-brown" data-id="element-704">
                            {selectedDealData.timeline}
                          </p>
                        </div>
                      </div>
                      <div className="bg-leaf-green/10 rounded-md p-3" data-id="element-705">
                        <p className="text-xs text-gray-600" data-id="element-706">Match Score</p>
                        <p className="font-semibold text-leaf-green" data-id="element-707">
                          {selectedDealData.matchScore}% Compatible
                        </p>
                      </div>
                    </div>
                    <div data-id="element-708">
                      <h4 className="font-medium mb-2" data-id="element-709">Project Description</h4>
                      <p className="text-gray-600" data-id="element-710">
                        {selectedDealData.description}
                      </p>
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-8" data-id="element-711">
                    <button className="btn btn-primary flex-1" data-id="element-712">
                      Accept Deal
                    </button>
                    <button className="btn btn-outline flex-1" data-id="element-713">
                      Propose Changes
                    </button>
                    <button className="btn border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex-1" data-id="element-714">
                      Decline
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </Layout>;
};